import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Input, ClaimExistsModal } from '@rabbit/elements/shared-components';
import {
  ConsumerIssueTypeHookOrigins,
  useManageConsumerIssueTypes,
  useSageAPI,
} from '@rabbit/bizproc/react';
import { CaseflowCaseTypes, RegisterCaseOrigins } from '@rabbit/bizproc/core';
import { t } from 'i18next';
import {
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { toast } from 'react-toastify';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { useState } from 'react';
import { CaseFlow_Utils_CheckClaimExists } from '@rabbit/bizproc/react';

export const validationSchema = Yup.object().shape({
  registrationNo: Yup.string()
    .trim()
    .min(5, 'Enter a valid registration plate')
    .required('Enter a registration plate'),
});

export interface NewClaimDescriptionProps {
  handleClose: () => void;
  onChange: any; //TODO
  setIsLoading: (loading: boolean) => void;
  data: any;
}

interface HoldingDataCase {
  consumer_issue_description: string;
  consumer_issue_type_ref: string;
  internal_comment: string;
}

export function NewClaimDescription(props: NewClaimDescriptionProps) {
  const { initiateCaseFlowCase } = useSageAPI();
  const { onChange, handleClose, setIsLoading, data } = props;
  const tenantLink = t('tenantLink');
  const [showExistingClaimModal, setShowExistingClaimModal] = useState(false);
  const [formValues, setFormValues] = useState<HoldingDataCase | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingValues, setPendingValues] = useState<HoldingDataCase | null>(null);


  const validationSchema = Yup.object().shape({
    consumer_issue_type_ref: Yup.string()
      .trim()
      .required('Issue type must be selected'),
    consumer_issue_description: Yup.string().trim(),
    internal_comment: Yup.string().trim(),
  });

  const { consumerIssueTypeList } = useManageConsumerIssueTypes(
    ConsumerIssueTypeHookOrigins.SAGE
  );
  const consumerIssueTypeOptions = !consumerIssueTypeList?.data
    ? []
    : consumerIssueTypeList.data.map((issue) => ({
        id: issue.docid,
        value: issue.docid,
        label: issue.label,
      })) ?? [];

  const initialValues: HoldingDataCase = {
    consumer_issue_type_ref: '',
    consumer_issue_description: '',
    internal_comment: '',
  };

  const onConfirmNewClaim = async () => {
    if (!pendingValues) {
      toast.error('Unable to create claim: No form data available');
      return;
    };
    
    setShowExistingClaimModal(false);
    // Wait for state to update before proceeding
    await new Promise(resolve => setTimeout(resolve, 0));
  
    // Re-run submission with supersede flag
    await handleCreateClaim(pendingValues, true);
  };

  const onKeepExistingClaim = () => {
    setShowExistingClaimModal(false);
    setPendingValues(null);
    setIsSubmitting(false);
    handleClose();
  };

  const handleCreateClaim = async (values: HoldingDataCase, supersedePreviousClaim: boolean) => {
    const consumer_issue_type = consumerIssueTypeOptions.find(
      (issue) => issue.value === values.consumer_issue_type_ref
    )?.label;

    const formData = {
      ...values,
      consumer_issue_type: consumer_issue_type ?? '',
    };

    const warrantorLink =
      PersonaTypeSingleLetter.Warrantor + PersonaIdTypeSplitter + tenantLink;
    try {
      setIsLoading(true);
      const response = await initiateCaseFlowCase({
        origin: RegisterCaseOrigins.SAGE_LITE,
        caseType: CaseflowCaseTypes.LITE,
        tenantLink,
        holdingLink: data.holding.holdingLink,
        consumerLink: data.holding.consumer,
        formData,
        warrantorLink:
          PersonaTypeSingleLetter.Warrantor +
          PersonaIdTypeSplitter +
          tenantLink, // we should add this to holding proxy (outside of the warranty arrays for ease of querying)
        supersedeOldClaim: supersedePreviousClaim 
      });
  
      if (response?.caseId) {
        toast.success('Claim created successfully');
        setTimeout(() => {
          handleClose();
          window.location.href =
            SAGE_ROUTE_NAME.CLAIMS + `/${response?.caseId}`;
        }, 1000);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(t('An error occurred while creating your claim. Please try again later.'));
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }

  }

  const onSubmit = async (values: HoldingDataCase) => {
    setFormValues(values);
    setIsSubmitting(true);
    try {
      const claimExists = await CaseFlow_Utils_CheckClaimExists(data.holding.holdingLink);
      if (claimExists.hasActiveClaim) {
        setPendingValues(values);
        setShowExistingClaimModal(true);
      } else {
        await handleCreateClaim(values, false);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('An error occurred while creating your claim. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, values }) => {
          return (
            <Form className="font-nunito flex flex-col gap-5 p-4">
              <Input
                type="select"
                name="consumer_issue_type_ref"
                label="Please select an issue*"
                settings={{
                  options: consumerIssueTypeOptions,
                  placeholder: 'Select',
                  id: 'consumer_issue_type_ref',
                  hint: '*required',
                }}
              />
              <Input
                label="Additional information"
                type="text"
                name="consumer_issue_description"
                settings={{
                  id: 'consumer_issue_description',
                  placeholder: 'Write text here…',
                  allowSpecialCharacter: true,
                }}
              />
              <Input
                type="rich-text"
                label="Internal comment"
                name="internal_comment"
                settings={{
                  id: 'internal_comment',
                  placeholder: '',
                  allowSpecialCharacter: true,
                }}
              />
              <div className="sticky bottom-0 -mb-2 flex w-full gap-4 bg-white py-4">
                <Button
                  kind="primary"
                  type="submit"
                  disabled={Object.keys(errors).length > 0}
                >
                  Continue
                </Button>
                <Button kind="red" type="submit" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
      {showExistingClaimModal && (
        <ClaimExistsModal 
          isOpen={showExistingClaimModal}
          onClose={onKeepExistingClaim}
          onConfirm={onConfirmNewClaim}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}

export default NewClaimDescription;
